import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button, ErrorState, Flex, HeaderItalic, ScrollArea, Text } from '@/components/common';
import { NotesGrid } from '@/components/notes';
import { useCurrentUser } from '@/contexts';
import { useAvailableToday, useNotes } from '@/hooks';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { analyticsClient } from '@/lib';
import { noteService } from '@/services';

export function NotesPage() {
  const { t } = useTranslation('notes');
  const { user, currentUser } = useCurrentUser();
  const { isLive, setLive } = useAvailableToday();
  const { myNote, entries, isError, refetch } = useNotes(user);
  const scrollRef = useScrollRestoration<HTMLDivElement>('notes');

  return (
    <Flex direction="column" height="100%">
      <ScrollArea ref={scrollRef}>
        <Flex direction="column" px="5" pb="6" pt="2">
          <Flex direction="column" gap="1" className="pt-safe-area">
            <HeaderItalic>{t('header.title')}</HeaderItalic>
            <Text size="1" color="gray">
              {t('header.subtitle')}
            </Text>
          </Flex>

          <Button
            type="button"
            variant={isLive ? 'solid' : 'outline'}
            color={isLive ? undefined : 'gray'}
            highContrast={!isLive}
            radius="full"
            size="md"
            mt="3"
            aria-pressed={isLive}
            onClick={() => {
              const next = !isLive;
              setLive(next);
              if (next && !myNote && currentUser) {
                // The live flag follows via syncUserNote once the profile write lands.
                noteService
                  .upsertNote(currentUser.uid, t('header.pulpMe'))
                  .then(() =>
                    analyticsClient.capture('note_created', {
                      source: 'pulp_me',
                      session_id: analyticsClient.getCurrentSessionId(),
                    })
                  )
                  .catch(() => toast.error(t('composer.error')));
              }
            }}
            data-testid="pulp-me-toggle"
          >
            {t('header.pulpMe')}
          </Button>

          {isError ? (
            <ErrorState action={{ label: t('actions.retry'), onClick: () => refetch() }} />
          ) : (
            <Flex mt="5" direction="column">
              <NotesGrid myNote={myNote} entries={entries} />
            </Flex>
          )}
        </Flex>
      </ScrollArea>
    </Flex>
  );
}
